<template>
  <Link
    :href="getRoute('page.certification.show', certification.slug)"
    class="group bg-white rounded-md shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100 hover:border-primary/30 animate-scale-in certification-card block no-underline hover:no-underline">
    <!-- Header with Gradient Background (instead of image) -->
    <div class="relative">
      <div class="h-48 relative overflow-hidden cursor-pointer">
        <!-- Dynamic Certificate Background -->
        <div class="w-full h-full flex items-center justify-center">
          <img
            :src="
              certification?.acronym?.toUpperCase() === 'CEA'
                ? '/images/certificates/c64_badge_cea.png'
                : certification?.acronym?.toUpperCase() === 'CEP'
                  ? '/images/certificates/c64_badge_cep.png'
                  : certification?.acronym?.toUpperCase() === 'CAEIS'
                    ? '/images/certificates/c64_badge_caeis.png'
                    : certification?.acronym?.toUpperCase() === 'SMARTER'
                      ? '/images/certificates/c64_badge_smarter.png'
                      : '/images/certificates/c64_default_badge.jpg'
            "
            :alt="certification.name"
            class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
          <div class="absolute inset-0"></div>
        </div>
        <div class="absolute inset-0"></div>
      </div>
    </div>

    <!-- Content Section - Flexible to push footer down -->
    <div class="certification-content">
      <!-- Certification Title -->
      <h3 class="_text:primary text-2xl font-bold px-4 mb-2 mt-4 leading-tight">
        {{ certification.name }}
      </h3>

      <!-- Certification Description -->
      <div class="p-4 cursor-pointer">
        <p
          class="text-md _text:normal _text:dark-35 mb-0 leading-relaxed truncated-description"
          v-html="truncateDescription(certification.description)"></p>
      </div>
    </div>

    <!-- Tags Section - Add Accredited tag when is_verified is true -->
    <div class="flex flex-wrap gap-2 px-4 mb-4">
      <span
        v-if="certification.is_verified"
        class="px-3 py-2 bg-success text-white text-xs rounded-md">
        ACCREDITED
      </span>
    </div>
  </Link>
</template>

<script setup>
  import { Link } from '@inertiajs/vue3'

  const props = defineProps({
    certification: {
      type: Object,
      required: true,
    },
  })

  // Utility functions
  const truncateDescription = (description) => {
    if (!description) return ''

    // Remove HTML tags for length calculation
    const plainText = description.replace(/<[^>]*>/g, '')
    const maxLength = 300

    if (plainText.length <= maxLength) {
      return description
    }

    // Let CSS handle truncation with line-clamp for better display
    return description
  }

  // Route helper function
  const getRoute = (name, params = {}) => {
    const routes = {
      'page.certification.show': `/page/certifications/${params}`,
    }
    return routes[name] || '/'
  }
</script>

<style scoped>
  .certification-card {
    transition:
      transform 0.2s ease-in-out,
      box-shadow 0.2s ease-in-out;
    /* Make card a flex container */
    display: flex;
    flex-direction: column;
    height: 100%;
    text-decoration: none !important;
  }

  .certification-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    text-decoration: none !important;
  }

  /* Content section grows to fill available space */
  .certification-content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  /* Animations */
  @keyframes scaleIn {
    from {
      transform: scale(0.9);
      opacity: 0;
    }
    to {
      transform: scale(1);
      opacity: 1;
    }
  }

  .animate-scale-in {
    animation: scaleIn 0.2s ease-out;
  }

  /* Truncated description */
  .truncated-description {
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* Primary colors */
  .bg-primary {
    background-color: #009ddb;
  }
  .bg-primary-dark {
    background-color: #007bb5;
  }
  .text-primary {
    color: #009ddb;
  }
  .text-primary-dark {
    color: #007bb5;
  }
  .border-primary {
    border-color: #009ddb;
  }
  .border-primary\/30 {
    border-color: rgba(0, 157, 219, 0.3);
  }

  /* Success colors */
  .bg-success {
    background-color: #10b981;
  }
  .text-success {
    color: #10b981;
  }
  .border-success {
    border-color: #10b981;
  }
  .bg-success\/20 {
    background-color: rgba(16, 185, 129, 0.2);
  }

  /* Hover states */
  .hover\:bg-primary-dark:hover {
    background-color: #007bb5;
  }
  .hover\:text-primary-dark:hover {
    color: #007bb5;
  }
  .hover\:scale-\[1\.02\]:hover {
    transform: scale(1.02);
  }
  .hover\:no-underline:hover {
    text-decoration: none;
  }

  /* Remove underline from link wrapper */
  .certification-card:hover {
    text-decoration: none;
  }

  /* Utility class for no underline */
  .no-underline {
    text-decoration: none !important;
  }

  /* Responsive design */
  @media screen and (max-width: 768px) {
    .certification-card {
      margin-bottom: 1rem;
    }
  }
</style>
